import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Upload, Loader2, AlertCircle, Copy, Search, MessageCircle } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { ISSUE_CATEGORIES, PREFERRED_CONTACT } from "@/data/constants";
import { useLanguage } from "@/context/LanguageContext";
import { submitWardIssue, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";

const empty = { name: "", phone: "", email: "", locality: "", ward: "Hoodi Ward 29", category: "Road", title: "", description: "", image: "", preferredContact: "Phone Call", consent: false };

export default function SubmitWardIssue() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const { tr } = useLanguage();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8MB");
    try {
      set("image", await compressImage(file, { maxDim: 1280, quality: 0.78 }));
    } catch {
      toast.error("Could not read the image");
    }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(result?.referenceId || "");
    toast.success("Reference ID copied");
  };

  const whatsappShare = () => {
    const text = `I reported a civic issue in Hoodi Ward 29 to Girish Kumar Naidu's public service team.\nReference: ${result?.referenceId}\nTrack it here: ${window.location.origin}/track-issue?ref=${result?.referenceId}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please provide consent to submit.");
    if (!form.name || !form.phone || !form.locality || !form.title || !form.description)
      return toast.error("Please fill all required fields.");
    setSubmitting(true);
    try {
      const res = await submitWardIssue(form);
      setResult(res);
      toast.success("Issue submitted successfully");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="submit-ward-issue-page">
      <PageSeo
        slug="submit-ward-issue"
        title="Submit Hoodi Ward 29 Issue | Girish Kumar Naidu"
        description="Submit your local Hoodi Ward 29 issue — roads, drainage, water, garbage, streetlights, parks, public safety, health, education — directly to Girish Kumar Naidu's public service team."
        keywords="submit Hoodi Ward 29 issue, Hoodi civic issues, Hoodi public grievance, GBA Hoodi Ward 29, Girish Kumar Naidu helpdesk"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Submit Hoodi Ward 29 Issue" }])]}
      />
      <PageHeader
        overline={tr("page.submit.overline")}
        title={tr("page.submit.title")}
        subtitle={tr("page.submit.subtitle")}
        crumbs={[{ name: "Submit Hoodi Ward 29 Issue" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {result ? (
            <div className="text-center py-10" data-testid="issue-success">
              <CheckCircle2 className="h-16 w-16 text-civic mx-auto mb-6" />
              <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight text-navy">Thank You</h2>
              <p className="mt-4 text-gray-600 max-w-md mx-auto">
                Your Hoodi Ward 29 issue has been received by Girish Kumar Naidu's public service team. Save your
                reference ID to track its status anytime.
              </p>

              <div className="mt-8 inline-flex flex-col items-center bg-paper border border-gray-200 rounded-2xl px-8 py-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Your Tracking Reference</p>
                <p className="font-mono text-2xl md:text-3xl font-bold text-gold tracking-widest" data-testid="issue-reference-id">
                  {result.referenceId}
                </p>
                <button
                  onClick={copyRef}
                  data-testid="copy-reference"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 text-navy text-sm font-bold hover:border-gold hover:text-gold transition-colors"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy Reference ID
                </button>
              </div>

              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <Link
                  to={`/track-issue?ref=${result.referenceId}`}
                  data-testid="track-issue-link"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy text-white font-bold hover:bg-navy-700 transition-colors"
                >
                  <Search className="h-4 w-4" /> Track This Issue
                </Link>
                <button
                  onClick={whatsappShare}
                  data-testid="share-whatsapp"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-civic text-white font-bold hover:bg-civic-dark transition-colors"
                >
                  <MessageCircle className="h-4 w-4" /> Share on WhatsApp
                </button>
                <button
                  onClick={() => { setForm(empty); setResult(null); }}
                  className="px-6 py-3.5 rounded-full border-2 border-navy text-navy font-bold hover:bg-navy hover:text-white transition-colors"
                >
                  Submit Another Issue
                </button>
              </div>
              <p className="mt-5 text-xs text-gray-400 max-w-sm mx-auto">
                Share your reference with neighbours facing the same issue — more reports help prioritise faster action.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="ward-issue-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label={tr("form.fullName")} testid="issue-name" value={form.name} onChange={(v) => set("name", v)} />
                <Field label={tr("form.phone")} testid="issue-phone" value={form.phone} onChange={(v) => set("phone", v)} />
                <Field label={tr("form.locality")} testid="issue-locality" value={form.locality} onChange={(v) => set("locality", v)} />
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">{tr("form.preferredContact")}</label>
                  <select
                    data-testid="issue-preferred-contact"
                    value={form.preferredContact}
                    onChange={(e) => set("preferredContact", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none bg-white"
                  >
                    {PREFERRED_CONTACT.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">{tr("form.category")}</label>
                  <select
                    data-testid="issue-category"
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none bg-white"
                  >
                    {ISSUE_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <Field label={tr("form.title")} testid="issue-title" value={form.title} onChange={(v) => set("title", v)} />
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">{tr("form.description")}</label>
                <textarea
                  data-testid="issue-description"
                  rows={5}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">{tr("form.photo")}</label>
                <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-gray-300 cursor-pointer hover:border-gold transition-colors">
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-500">{form.image ? "Photo attached ✓" : "Click to upload an image (max 8MB)"}</span>
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="issue-image" />
                </label>
                {form.image && <img src={form.image} alt="preview" className="mt-3 h-32 rounded-xl object-cover" />}
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="issue-consent"
                  checked={form.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-gold focus:ring-gold"
                />
                <span className="text-sm text-gray-600">{tr("form.consent")}</span>
              </label>
              <button
                type="submit"
                disabled={submitting}
                data-testid="issue-submit-button"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-gold text-white font-bold btn-saffron disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <AlertCircle className="h-5 w-5" />}
                {submitting ? tr("form.submitting") : tr("cta.submitIssue")}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-2">{label}</label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none"
      />
    </div>
  );
}
